import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { catchError, filter, forkJoin, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import { FUNNEL_TYPE, USER_COMPANY_STATUS } from '../enum/constants';
import { createQueryParams } from '../helpers';
import { BaseService } from './base.service';
import { BoardCompanyService } from './board-company.service';
import { BoardContactService } from './board-contact.service';
import { BoardService } from './board.service';
import { DialogService } from './dialog.service';
import { NotificationService } from './notifications.service';
import { StackedSidebarService } from './stacked-sidebar.service';
import { EmployeeProfile } from './crm.service';
import { SidebarType } from '../components/layout/stacked-sidebar/stacked-sidebar.interface';
import { BoardFunnel } from '../components/board-v3/board-v3.types';
import {
  formatContactPhone,
  getContactEmails,
  getContactName,
  getContactPhones,
} from '../components/board-v3/contacts/contact.utils';
import { getCompanyEmails, getCompanyName, getCompanyPhones } from '../components/board-v3/companies/company.utils';
import { getApiError, toDayIso } from '../components/warehouse/warehouse.utils';
import {
  CreateTaskPayload,
  ResolvedTaskLink,
  Task,
  TaskDetailPayload,
  TaskDetailResult,
  TaskFormPayload,
  TaskLink,
  TaskLinkTarget,
  TaskReminder,
  TaskReminderPayload,
  TasksPage,
  TasksQuery,
  TaskStatus,
  TaskTimelineEvent,
  TaskUser,
  TodayTasks,
  UpdateTaskPayload,
} from '../components/tasks/tasks.types';
import { compareDeadlines, getPersonName, interleave } from '../components/tasks/tasks.utils';

/** The employees change rarely: the screens that open one after another share one request */
const USERS_CACHE_MS = 60_000;
/** The most results of one kind in the link picker; the cards are taken from the funnels in turns */
const LINK_SEARCH_LIMIT = 5;

/** The biggest page the backend gives: the tasks of the day are not paged, so a day with more is cut and says so */
const TODAY_TASKS_TAKE = 100;

/** A 409 or a missing task: what is on the screen is out of date and has to be read again */
export const isStaleTaskError = (code: string | undefined): boolean =>
  code === 'TASK_STATUS_CONFLICT' || code === 'TASK_NOT_FOUND';

/** The lists of the query go as comma separated values, which the backend accepts */
function toQueryParams({ status, priority, ...rest }: TasksQuery): object {
  return {
    ...rest,
    ...(status?.length ? { status: status.join(',') } : {}),
    ...(priority?.length ? { priority: priority.join(',') } : {}),
  };
}

/**
 * Tasks: their API (`/task`: tasks, status, comments, reminders), the people and the entities they are about, and the
 * feedback of the task screens: translated toasts, confirmations and readable API errors.
 */
@Injectable({ providedIn: 'root' })
export class TaskService {
  private readonly http = inject(HttpClient);
  private readonly baseService = inject(BaseService);
  private readonly boardService = inject(BoardService);
  private readonly contactService = inject(BoardContactService);
  private readonly companyService = inject(BoardCompanyService);
  private readonly stackedSidebarService = inject(StackedSidebarService);
  private readonly dialogService = inject(DialogService);
  private readonly notificationService = inject(NotificationService);
  private readonly translateService = inject(TranslateService);

  private usersCache?: { loadedAt: number; users$: Observable<TaskUser[]> };

  // #region tasks

  /** Nearest deadline first, tasks without a deadline last, then the newer ones */
  readonly getTasks = (query: TasksQuery): Observable<TasksPage> =>
    this.request('GET', 'task', null, toQueryParams(query));
  readonly getTask = (taskId: string): Observable<Task> => this.request('GET', `task/${taskId}`);
  /** The creator is the current user and the status is `NEW` */
  readonly createTask = (payload: CreateTaskPayload): Observable<Task> => this.request('POST', 'task', payload);
  readonly updateTask = (taskId: string, payload: UpdateTaskPayload): Observable<Task> =>
    this.request('PUT', `task/${taskId}`, payload);
  readonly deleteTask = (taskId: string): Observable<void> =>
    this.request<unknown>('DELETE', `task/${taskId}`).pipe(map(() => undefined));
  /** 409 `TASK_STATUS_CONFLICT` for a move the status forbids, 403 `TASK_REVIEW_CREATOR_ONLY` for a review of another */
  readonly changeStatus = (taskId: string, status: TaskStatus): Observable<Task> =>
    this.request('PUT', `task/${taskId}/status`, { status });

  /**
   * What the current user has to do by the end of today: the open tasks they are the executor of that are due today
   * or are overdue, and the tasks they created that wait for their review (due or overdue as well). The nearest
   * deadline first. Postponed tasks are not here: they were put off on purpose.
   */
  getTodayTasks(now: Date = new Date()): Observable<TodayTasks> {
    const userId = this.currentUserId;
    if (!userId) return of({ items: [], total: 0 });

    // A task without a deadline is not in a list that has a deadline limit, and the overdue ones all are
    const upTo = { deadline_to: toDayIso(now, true), take: TODAY_TASKS_TAKE };
    const todo$ = this.getTasks({ ...upTo, assignee_id: userId, status: ['NEW', 'IN_PROGRESS'] });
    // The decision is the creator's: it is the one thing a task on review needs from them
    const toReview$ = this.getTasks({ ...upTo, created_by: userId, status: ['REVIEW'] }).pipe(
      // The tasks that wait for the review are an addition: the day is still shown without them
      catchError(() => of({ items: [], total: 0, size: 0, page: 1 } as TasksPage)),
    );

    return forkJoin([todo$, toReview$]).pipe(
      map(([todo, review]) => ({
        items: [...todo.items, ...review.items].sort(compareDeadlines),
        total: todo.total + review.total,
      })),
    );
  }

  // #endregion

  // #region comments, history and reminders

  /** From the newest to the oldest event, without pages */
  readonly getTimelines = (taskId: string): Observable<TaskTimelineEvent[]> =>
    this.request<TaskTimelineEvent[] | null>('GET', `task/${taskId}/timelines`).pipe(map((events) => events ?? []));
  readonly createComment = (taskId: string, text: string): Observable<TaskTimelineEvent> =>
    this.request('POST', `task/${taskId}/comment`, { text });
  readonly updateComment = (taskId: string, commentId: string, text: string): Observable<TaskTimelineEvent> =>
    this.request('PUT', `task/${taskId}/comment/${commentId}`, { text });
  readonly deleteComment = (taskId: string, commentId: string): Observable<void> =>
    this.request<unknown>('DELETE', `task/${taskId}/comment/${commentId}`).pipe(map(() => undefined));

  /** A time that is already among the reminders of the task is not added twice: the existing one comes back */
  readonly createReminder = (taskId: string, payload: TaskReminderPayload): Observable<TaskReminder> =>
    this.request('POST', `task/${taskId}/reminder`, payload);
  readonly deleteReminder = (taskId: string, reminderId: string): Observable<void> =>
    this.request<unknown>('DELETE', `task/${taskId}/reminder/${reminderId}`).pipe(map(() => undefined));

  // #endregion

  // #region people

  /** The id of the current user, the one a created task is about to belong to */
  get currentUserId(): string | null {
    const profile = this.baseService.currentUserInfo as { id?: string | number } | undefined;
    const id = profile?.id ?? this.baseService.user?.id;
    return id === undefined || id === null ? null : String(id);
  }

  /** Cards, contacts and companies, which a task can be linked to, belong to CRM 2.0 */
  get isCrmV2(): boolean {
    const company = this.baseService.currentCompanyInfo as { settings?: { is_crm_v2?: boolean } } | undefined;
    return !!company?.settings?.is_crm_v2;
  }

  /**
   * The employees that are, or were, able to work: pickers offer the active ones, and the suspended ones are here to
   * show a name next to a task they had before. Invited ones have not joined yet.
   */
  getUsers(): Observable<TaskUser[]> {
    const cached = this.usersCache;
    if (cached && Date.now() - cached.loadedAt < USERS_CACHE_MS) return cached.users$;

    const users$ = this.http
      .get<{ data: EmployeeProfile[] | { items?: EmployeeProfile[] } }>(`${environment.baseURL}/employees`, {
        params: {
          page: 1,
          take: 500,
          status: `${USER_COMPANY_STATUS.ACTIVE},${USER_COMPANY_STATUS.SUSPENDED}`,
        },
      })
      .pipe(
        map(({ data }) => (Array.isArray(data) ? data : (data?.items ?? []))),
        map((employees) =>
          employees.map((employee): TaskUser => ({
            id: employee.id,
            name: getPersonName(employee) || employee.id,
            avatarUrl: employee.avatar_url || undefined,
            isActive: !employee.membership_status || employee.membership_status === USER_COMPANY_STATUS.ACTIVE,
          })),
        ),
        // The error resets the share: the next screen asks again
        shareReplay(1),
      );
    this.usersCache = { loadedAt: Date.now(), users$ };
    return users$;
  }

  // #endregion

  // #region linked entities

  /** The names and the types of what the links point to; the ones that can not be loaded come back as `missing` */
  resolveLinks(links: readonly TaskLink[]): Observable<ResolvedTaskLink[]> {
    if (!links.length) return of([]);

    const leadFunnelIds$ = links.some((link) => link.entity_type === 'card')
      ? this.boardService.getFunnels({ type: FUNNEL_TYPE.LEAD }).pipe(
          map((funnels) => new Set(funnels.map((funnel) => funnel.id))),
          catchError(() => of(new Set<string>())),
        )
      : of(new Set<string>());

    return leadFunnelIds$.pipe(
      switchMap((leadFunnelIds) => forkJoin(links.map((link) => this.resolveLink(link, leadFunnelIds)))),
    );
  }

  /**
   * Leads, deals, contacts and companies that match the text. The backend has no search over all the cards, so every
   * funnel is asked for the cards with that name.
   */
  searchLinkTargets(search: string, funnels: readonly BoardFunnel[]): Observable<TaskLinkTarget[]> {
    const cards$ = funnels.length
      ? forkJoin(
          funnels.map((funnel) =>
            this.boardService
              .getAllCards({
                funnel_id: funnel.id,
                limit: LINK_SEARCH_LIMIT,
                filters: [{ key: 'name', type: 'ilike', value: search, is_custom: false }],
              })
              .pipe(
                map((cards) =>
                  cards.map((card): TaskLinkTarget => ({
                    entity_type: 'card',
                    entity_id: card.id,
                    name: card.name,
                    funnelType: funnel.type,
                    subtitle: funnel.name,
                  })),
                ),
                catchError(() => of([] as TaskLinkTarget[])),
              ),
          ),
        ).pipe(map((groups) => interleave(groups, LINK_SEARCH_LIMIT)))
      : of([] as TaskLinkTarget[]);

    const contacts$ = this.contactService.getContacts({ search, page: 1, take: LINK_SEARCH_LIMIT }).pipe(
      map(({ items }) =>
        items.map((contact): TaskLinkTarget => ({
          entity_type: 'contact',
          entity_id: contact.id,
          name: getContactName(contact),
          subtitle: [...getContactPhones(contact).map(formatContactPhone), ...getContactEmails(contact)][0],
        })),
      ),
      catchError(() => of([] as TaskLinkTarget[])),
    );

    const companies$ = this.companyService.getCompanies({
      filters: [{ key: 'name', type: 'ilike', value: search, is_custom: false }],
      page: 1,
      take: LINK_SEARCH_LIMIT,
    }).pipe(
      map(({ items }) =>
        items.map((company): TaskLinkTarget => ({
          entity_type: 'company',
          entity_id: company.id,
          name: getCompanyName(company),
          subtitle: [...getCompanyPhones(company).map(formatContactPhone), ...getCompanyEmails(company)][0],
        })),
      ),
      catchError(() => of([] as TaskLinkTarget[])),
    );

    return forkJoin([cards$, contacts$, companies$]).pipe(map((groups) => groups.flat()));
  }

  private resolveLink(link: TaskLink, leadFunnelIds: Set<string>): Observable<ResolvedTaskLink> {
    const missing: ResolvedTaskLink = { ...link, name: '', missing: true };

    switch (link.entity_type) {
      case 'card':
        return this.boardService.getCardDetails(link.entity_id).pipe(
          map((card): ResolvedTaskLink => ({
            ...link,
            name: card.name,
            funnelType: leadFunnelIds.has(card.funnel_id) ? FUNNEL_TYPE.LEAD : FUNNEL_TYPE.DEAL,
          })),
          catchError(() => of(missing)),
        );
      case 'contact':
        return this.contactService.getContact(link.entity_id).pipe(
          map((contact): ResolvedTaskLink => ({ ...link, name: getContactName(contact) })),
          catchError(() => of(missing)),
        );
      case 'company':
        return this.companyService.getCompany(link.entity_id).pipe(
          map((company): ResolvedTaskLink => ({ ...link, name: getCompanyName(company) })),
          catchError(() => of(missing)),
        );
    }
  }

  // #endregion

  // #region sidebars

  /** The new task, or nothing when the form was closed */
  openTaskForm(payload: TaskFormPayload = {}): Observable<Task | undefined> {
    return this.stackedSidebarService.openSidebar(
      { type: SidebarType.TaskForm, width: '520px' },
      payload,
    ) as Observable<Task | undefined>;
  }

  openTaskDetail(taskId: string): Observable<TaskDetailResult | undefined> {
    const payload: TaskDetailPayload = {
      taskId,
      // A card detail is open already, and a second one would replace the shared `BoardService.card` of the first
      canOpenCards: !this.stackedSidebarService.isOpen(SidebarType.CardV3),
    };
    return this.stackedSidebarService.openSidebar(
      { type: SidebarType.TaskDetail, width: '90vw' },
      payload,
    ) as Observable<TaskDetailResult | undefined>;
  }

  // #endregion

  // #region feedback

  t(key: string, params?: Record<string, unknown>): string {
    return String(this.translateService.instant(key, params));
  }

  success(key: string, params?: Record<string, unknown>): void {
    this.notificationService.showSuccess('', this.t(key, params));
  }

  /** Toast of an API error; `contextKey` says what failed */
  error(error: unknown, contextKey?: string): void {
    const message = this.errorMessage(error);
    this.notificationService.showError('', contextKey ? `${this.t(contextKey)}<br>${message}` : message);
  }

  /** Emits once the translated question is confirmed, right away when there is no question */
  confirm(key: string | null, params?: Record<string, unknown>): Observable<unknown> {
    if (!key) return of(true);
    return this.dialogService.openConfirm({ text: this.t(key, params), isTranslate: true }).pipe(filter(Boolean));
  }

  /** The code of the API error, to tell a status conflict from the rest */
  getErrorCode(error: unknown): string | undefined {
    return getApiError(error).code;
  }

  /** Readable text of an API error: `crm_tasks.errors.<CODE>`, the default error for a code without a text */
  errorMessage(error: unknown): string {
    const { code } = getApiError(error);
    return (code && this.find(`crm_tasks.errors.${code}`)) || this.t('errors.default_error');
  }

  // #endregion

  /** The translation of the key, null when there is no text for it */
  private find(key: string): string | null {
    const text: unknown = this.translateService.instant(key);
    return typeof text === 'string' && text !== key ? text : null;
  }

  private request<T>(method: string, path: string, body?: unknown, query?: object): Observable<T> {
    return this.http
      .request<{ data: T }>(method, `${environment.baseURL}/${path}`, {
        body,
        params: query && createQueryParams(query),
      })
      .pipe(map((res) => res.data));
  }
}
